import { useState } from "react";
import axios from "axios";
import { Facebook, Instagram, Linkedin, Mail, MessageCircle, Send, Youtube } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/config/site";
import { Reveal } from "./Reveal";

const SOCIALS = [
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "facebook", label: "Facebook", icon: Facebook },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "youtube", label: "YouTube", icon: Youtube },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please email us directly at " + SITE.email);
    } finally {
      setSending(false);
    }
  };

  const openSocial = (id, label) => {
    const url = SITE.socials[id];
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.info(`Our ${label} page is launching soon.`);
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Contact</p>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl lg:text-4xl">
              Questions about DENSA? <span className="text-brand">Talk to us.</span>
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600">
              Whether you&apos;re a dentist curious about DENSA, a clinic team, or a future partner — we&apos;d love to
              hear from you.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <a
              href={`mailto:${SITE.supportEmail}`}
              data-testid="contact-email-link"
              className="mt-8 flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-mint">
                <Mail className="h-5 w-5 text-brand" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-xs font-medium uppercase tracking-widest text-slate-500">Email us</span>
                <span className="block text-base font-semibold text-brand-ink">{SITE.supportEmail}</span>
                <span className="block text-xs text-slate-500">Also reachable at {SITE.email}</span>
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.18}>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Follow DENSA</p>
            <div className="mt-3 flex flex-wrap gap-3">
              {SOCIALS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`social-link-${id}`}
                  onClick={() => openSocial(id, label)}
                  aria-label={`DENSA on ${label}`}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand hover:text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <Icon className="h-4.5 w-4.5 h-5 w-5" aria-hidden="true" />
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-[1.75rem] border border-slate-200/80 bg-white p-6 shadow-xl shadow-brand/5 sm:p-8"
          >
            <h3 className="text-lg font-semibold text-brand-ink">Send us a message</h3>
            <div className="mt-5 space-y-4">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Dr. Jane Doe"
                  className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-[#F8FAF9] px-4 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@clinic.com"
                  className="min-h-[48px] w-full rounded-xl border border-slate-200 bg-[#F8FAF9] px-4 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your clinic or ask us anything about DENSA…"
                  className="w-full rounded-xl border border-slate-200 bg-[#F8FAF9] px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                {sending ? "Sending…" : "Send message"}
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
